import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Container,
  SimpleGrid,
  Icon,
  Badge,
  Divider,
} from '@chakra-ui/react';
import React, { useContext, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { CartContext } from '../Context/CartContext';
import {
  FiCheckCircle,
  FiPackage,
  FiMapPin,
  FiCalendar,
  FiShoppingBag,
  FiPhoneCall,
  FiFileText,
  FiArrowRight,
} from 'react-icons/fi';

const ConsratsPage = () => {
  const amount = localStorage.getItem("Total") || "0";
  const orderId = localStorage.getItem("order_id") || "";
  const { globalAddress } = useContext(CartContext);
  const tracking = Math.ceil((Math.random() * 117) + 1578154424840);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatDate = () => {
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + 5);
    return deliveryDate.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box bg="#F8FAFC" minH="90vh" py={{ base: 8, md: 14 }}>
      <Container maxW="4xl" px={{ base: 4, md: 6 }}>
        {/* Celebration Header Card */}
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 10 }}
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
          position="relative"
          overflow="hidden"
          mb={6}
        >
          {/* Top highlight bar */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="6px"
            bg="linear-gradient(90deg, #10B981 0%, #059669 50%, #EAB308 100%)"
          />

          <Box
            w="80px"
            h="80px"
            mx="auto"
            mb={5}
            borderRadius="full"
            bg="emerald.50"
            color="emerald.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 0 0 12px rgba(16, 185, 129, 0.08)"
          >
            <Icon as={FiCheckCircle} boxSize={10} color="#059669" />
          </Box>

          <Badge
            bg="rgba(16, 185, 129, 0.1)"
            color="#059669"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            textTransform="uppercase"
            mb={3}
          >
            Payment Confirmed & Verified
          </Badge>

          <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color="#162237" mb={2}>
            Thank You for Your Order!
          </Text>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="xl" mx="auto">
            Your judicial publications and legal equipment order has been confirmed. A formal confirmation and tax invoice have been dispatched to your email.
          </Text>

          {/* Amount Paid Pill */}
          <Box
            mt={6}
            display="inline-flex"
            alignItems="center"
            bg="#F8FAFC"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            px={5}
            py={3}
          >
            <Text fontSize="sm" color="gray.500" mr={2}>
              Total Amount Paid:
            </Text>
            <Text fontSize="xl" fontWeight="extrabold" color="#162237">
              ₹{Number(amount).toLocaleString('en-IN')}
            </Text>
          </Box>
        </Box>

        {/* Order Details Grid */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          {/* Tracking & Delivery Schedule */}
          <Box bg="white" borderRadius="2xl" p={{ base: 6, md: 7 }} border="1px solid" borderColor="gray.100" boxShadow="sm">
            <HStack spacing={3} mb={4}>
              <Box p={2.5} bg="blue.50" color="#162237" borderRadius="xl">
                <Icon as={FiPackage} boxSize={5} />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.400">
                  Fulfillment Status
                </Text>
                <Text fontSize="md" fontWeight="bold" color="#162237">
                  Dispatch & Courier Details
                </Text>
              </Box>
            </HStack>

            <VStack spacing={3.5} align="stretch" fontSize="sm">
              <HStack justify="space-between" py={1.5} borderBottom="1px dashed" borderColor="gray.100">
                <Text color="gray.500">Waybill / Tracking Ref:</Text>
                <Text fontWeight="semibold" color="#162237" fontFamily="monospace">
                  #{tracking}
                </Text>
              </HStack>
              {orderId && (
                <HStack justify="space-between" py={1.5} borderBottom="1px dashed" borderColor="gray.100">
                  <Text color="gray.500">Official Order ID:</Text>
                  <Text fontWeight="semibold" color="blue.600" fontFamily="monospace">
                    #{orderId}
                  </Text>
                </HStack>
              )}
              <HStack justify="space-between" py={1.5} borderBottom="1px dashed" borderColor="gray.100">
                <HStack spacing={2} color="gray.500">
                  <Icon as={FiCalendar} />
                  <Text>Estimated Delivery:</Text>
                </HStack>
                <Text fontWeight="bold" color="#059669">
                  By {formatDate()}
                </Text>
              </HStack>
              <HStack justify="space-between" py={1.5}>
                <Text color="gray.500">Courier Partner:</Text>
                <Text fontWeight="medium" color="#162237">
                  BlueDart / Delhivery Express
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Delivery Address */}
          <Box bg="white" borderRadius="2xl" p={{ base: 6, md: 7 }} border="1px solid" borderColor="gray.100" boxShadow="sm">
            <HStack spacing={3} mb={4}>
              <Box p={2.5} bg="amber.50" color="#B45309" borderRadius="xl" bg="#FEF3C7">
                <Icon as={FiMapPin} boxSize={5} />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.400">
                  Destination
                </Text>
                <Text fontSize="md" fontWeight="bold" color="#162237">
                  Delivery Chambers / Office
                </Text>
              </Box>
            </HStack>

            <VStack align="stretch" spacing={2} fontSize="sm">
              <Text fontWeight="bold" color="#162237" fontSize="md">
                {globalAddress?.Name || 'Consignee Name'}
              </Text>
              <Text color="gray.600" lineHeight="tall">
                {globalAddress?.Address || 'Registered Chambers Address'}
              </Text>
              {globalAddress?.Number && (
                <Text color="gray.500" pt={2}>
                  <strong>Mobile:</strong> {globalAddress.Number}
                </Text>
              )}
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Action Buttons */}
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 8 }}
          border="1px solid"
          borderColor="gray.100"
          boxShadow="sm"
        >
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
            {orderId && (
              <Button
                as={NavLink}
                to={`/invoice?orderId=${orderId}`}
                size="lg"
                variant="outline"
                borderColor="gray.200"
                color="#162237"
                leftIcon={<FiFileText />}
                _hover={{ bg: 'gray.50', borderColor: '#162237' }}
              >
                Tax Invoice
              </Button>
            )}
            <Button
              as={NavLink}
              to="/orderpage"
              size="lg"
              bg="#162237"
              color="white"
              rightIcon={<FiArrowRight />}
              _hover={{ bg: '#0B132B' }}
            >
              View Order History
            </Button>
            <Button
              as={NavLink}
              to="/"
              size="lg"
              variant="outline"
              borderColor="gray.200"
              color="gray.700"
              leftIcon={<FiShoppingBag />}
              _hover={{ bg: 'gray.50' }}
            >
              Continue Shopping
            </Button>
          </SimpleGrid>

          <Divider my={6} />

          <HStack justify="center" spacing={2} color="gray.500" fontSize="xs">
            <Icon as={FiPhoneCall} />
            <Text>
              Questions about this order? Contact Chambers Support Desk at <strong>+91 98111 23456</strong> or <strong>support@navneetlaw.com</strong>
            </Text>
          </HStack>
        </Box>
      </Container>
    </Box>
  );
};

export default ConsratsPage;